import { supabase } from './supabaseClient';
import { requestDeduplicator } from '../utils/requestDeduplicator';
import type {
  ForumCategory,
  ForumTopic,
  ForumPost,
  ForumNotification,
  ForumPostLike,
  ForumPostReport,
  ModerationAction,
  UserForumBadge,
  ForumRule,
  ForumPoll,
  ForumPollOption
} from '../types/forum';

// Cache interface for performance optimization
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

class SimpleCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl = this.defaultTTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }
}

export interface CreateCategoryData {
  title: string;
  description: string;
  icon: string;
  color: string;
  subcategories: string[];
  view_permission: 'public' | 'members' | 'premium' | 'admin';
  post_permission: 'public' | 'members' | 'premium' | 'moderators' | 'admin';
  requires_approval: boolean;
}

export interface CreateTopicData {
  category_id: string;
  title: string;
  description?: string;
  is_pinned?: boolean;
  is_announcement?: boolean;
  tags?: string[];
  poll?: {
    question: string;
    options: string[];
  };
}

export interface CreatePostData {
  topic_id: string;
  content: string;
  title?: string;
  parent_post_id?: string;
  attachments?: any[];
}

export interface UpdatePostData {
  content?: string;
  title?: string;
  attachments?: any[];
}

class ForumService {
  private cache = new SimpleCache();

  // Clear cache when data is modified
  private clearCacheForCategory(categoryId: string): void {
    this.cache.delete(`categories`);
    this.cache.delete(`category:${categoryId}`);
    this.cache.delete(`topics:${categoryId}`);
  }

  private clearCacheForTopic(topicId: string, categoryId?: string): void {
    if (categoryId) {
      this.cache.delete(`topics:${categoryId}`);
    }
    this.cache.delete(`topic:${topicId}`);
    this.cache.delete(`posts:${topicId}`);
  }

  private clearCacheForPost(postId: string, topicId: string): void {
    // Clear all posts cache entries for this topic (including paginated results)
    const keys = Array.from(this.cache['cache'].keys()).filter(key =>
      key.startsWith(`posts:${topicId}`)
    );
    keys.forEach(key => this.cache.delete(key));

    this.cache.delete(`post:${postId}`);
    this.cache.delete(`topic:${topicId}`); // Also clear topic cache since post count changed
  }

  // Categories
  async getCategories(includeInactive = false): Promise<ForumCategory[]> {
    const cacheKey = `categories:${includeInactive}`;
    const cachedData = this.cache.get<ForumCategory[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Use request deduplication to prevent multiple simultaneous requests
    return requestDeduplicator.dedupe(cacheKey, async () => {
      let query = supabase
        .from('forum_categories')
        .select(`
          *,
          created_by_user:users!forum_categories_created_by_fkey(username, display_name),
          last_activity_user:users!forum_categories_last_activity_by_fkey(username, display_name)
        `)
        .order('order_index', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;

      this.cache.set(cacheKey, data || []);
      return data || [];
    });
  }

  async getCategoryById(id: string): Promise<ForumCategory | null> {
    const cacheKey = `category:${id}`;
    const cachedData = this.cache.get<ForumCategory>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const { data, error } = await supabase
      .from('forum_categories')
      .select(`
        *,
        created_by_user:users!forum_categories_created_by_fkey(username, display_name),
        last_activity_user:users!forum_categories_last_activity_by_fkey(username, display_name)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    this.cache.set(cacheKey, data);
    return data;
  }

  async getCategoryBySlug(slug: string): Promise<ForumCategory | null> {
    const cacheKey = `category:slug:${slug}`;
    const cachedData = this.cache.get<ForumCategory>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    console.log('getCategoryBySlug: Searching for slug:', slug);

    const { data, error } = await supabase
      .from('forum_categories')
      .select('*')
      .eq('slug', slug)
      .single();

    console.log('getCategoryBySlug: Query result:', { data, error });

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('getCategoryBySlug: No rows found (PGRST116)');
        return null;
      }
      console.error('getCategoryBySlug: Database error:', error);
      throw error;
    }

    this.cache.set(cacheKey, data);
    console.log('getCategoryBySlug: Found and cached category:', data);
    return data;
  }

  // Temporary method to bypass RLS issues
  async getCategoryBySlugDirect(slug: string): Promise<ForumCategory | null> {
    console.log('getCategoryBySlugDirect: Searching for slug:', slug);

    try {
      // First try the RPC function
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_category_by_slug', { slug_param: slug });

      if (!rpcError && rpcData && rpcData.length > 0) {
        console.log('getCategoryBySlugDirect: RPC success:', rpcData[0]);
        return rpcData[0];
      }

      console.log('getCategoryBySlugDirect: RPC failed or no data, trying direct query:', rpcError);

      // Fallback to direct query
      const { data: directData, error: directError } = await supabase
        .from('forum_categories')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();

      if (directError) {
        console.error('getCategoryBySlugDirect: Direct query error:', directError);
        throw directError;
      }

      console.log('getCategoryBySlugDirect: Direct query result:', directData);
      return directData;
    } catch (err) {
      console.error('getCategoryBySlugDirect: Error:', err);
      throw err;
    }
  }

  async createCategory(categoryData: CreateCategoryData): Promise<ForumCategory> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const slug = categoryData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

    const { data, error } = await supabase
      .from('forum_categories')
      .insert({
        ...categoryData,
        slug,
        created_by: user.id,
        updated_by: user.id
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    this.cache.delete('categories');
    return data;
  }

  async updateCategory(id: string, updates: Partial<CreateCategoryData>): Promise<ForumCategory> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData: any = { ...updates, updated_by: user.id, updated_at: new Date().toISOString() };

    if (updates.title) {
      updateData.slug = updates.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    const { data, error } = await supabase
      .from('forum_categories')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    this.cache.delete(`category:${id}`);
    this.cache.delete('categories');
    return data;
  }

  async deleteCategory(id: string): Promise<void> {
    const { error } = await supabase
      .from('forum_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;

    // Invalidate cache
    this.cache.delete(`category:${id}`);
    this.cache.delete('categories');
  }

  // Topics
  async getTopics(categoryId: string, page = 1, limit = 20): Promise<{ topics: ForumTopic[]; totalCount: number }> {
    const offset = (page - 1) * limit;

    const cacheKey = `topics:${categoryId}:${page}:${limit}`;
    const cachedData = this.cache.get<{ topics: ForumTopic[]; totalCount: number }>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const [topicsQuery, countQuery] = await Promise.all([
      supabase
        .from('forum_topics')
        .select(`
          *,
          category:forum_categories!forum_topics_category_id_fkey(title, slug),
          created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
        `)
        .eq('category_id', categoryId)
        .order('is_pinned', { ascending: false })
        .order('last_activity_at', { ascending: false })
        .range(offset, offset + limit - 1),

      supabase
        .from('forum_topics')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', categoryId)
    ]);

    const { data: topics, error: topicsError } = topicsQuery;
    const { count, error: countError } = countQuery;

    if (topicsError) throw topicsError;
    if (countError) throw countError;

    const result = {
      topics: topics || [],
      totalCount: count || 0
    };

    this.cache.set(cacheKey, result);
    return result;
  }

  // Debug method to get topics without RLS (for testing)
  async getTopicsDebug(categorySlug: string): Promise<any[]> {
    console.log('getTopicsDebug: Checking topics for category slug:', categorySlug);

    try {
      // Get category first
      const { data: categories, error: categoryError } = await supabase
        .from('forum_categories')
        .select('id, title, slug')
        .eq('slug', categorySlug);

      console.log('getTopicsDebug: Categories query result:', { categories, categoryError });

      if (categoryError || !categories || categories.length === 0) {
        console.log('getTopicsDebug: No category found');
        return [];
      }

      const categoryId = categories[0].id;
      console.log('getTopicsDebug: Found category ID:', categoryId);

      // Get topics for this category
      const { data: topics, error: topicsError } = await supabase
        .from('forum_topics')
        .select('*')
        .eq('category_id', categoryId);

      console.log('getTopicsDebug: Topics query result:', { topics, topicsError });

      return topics || [];
    } catch (error) {
      console.error('getTopicsDebug: Error:', error);
      return [];
    }
  }

  // Get topics by category slug (more convenient for slug-based routing)
  async getTopicsByCategorySlug(categorySlug: string, page = 1, limit = 20): Promise<{ topics: ForumTopic[]; totalCount: number }> {
    console.log('getTopicsByCategorySlug: Loading topics for slug:', categorySlug);

    // First get the category to find its ID
    const category = await this.getCategoryBySlugDirect(categorySlug);
    if (!category?.id) {
      console.error('getTopicsByCategorySlug: Category not found for slug:', categorySlug);
      return { topics: [], totalCount: 0 };
    }

    console.log('getTopicsByCategorySlug: Found category:', category.id, category.title);

    // Then get topics using the category ID
    return this.getTopics(category.id, page, limit);
  }

  // Get topics by category slug with sorting support
  async getTopicsByCategorySlugWithSort(
    categorySlug: string,
    page = 1,
    limit = 20,
    sortBy: 'newest' | 'oldest' | 'most_active' | 'most_popular' = 'most_active'
  ): Promise<{ topics: ForumTopic[]; totalCount: number }> {
    console.log('getTopicsByCategorySlugWithSort: Loading topics for slug:', categorySlug, 'sortBy:', sortBy);

    // First get the category to find its ID
    const category = await this.getCategoryBySlugDirect(categorySlug);
    if (!category?.id) {
      console.error('getTopicsByCategorySlugWithSort: Category not found for slug:', categorySlug);
      return { topics: [], totalCount: 0 };
    }

    console.log('getTopicsByCategorySlugWithSort: Found category:', category.id, category.title);

    const offset = (page - 1) * limit;
    const cacheKey = `topics:${category.id}:${page}:${limit}:${sortBy}`;

    try {
      const [topicsQuery, countQuery] = await Promise.all([
        this.buildTopicsQuery(category.id, offset, limit, sortBy),
        supabase
          .from('forum_topics')
          .select('*', { count: 'exact', head: true })
          .eq('category_id', category.id)
      ]);

      const { data: topics, error: topicsError } = topicsQuery;
      const { count, error: countError } = countQuery;

      if (topicsError) throw topicsError;
      if (countError) throw countError;

      const result = {
        topics: topics || [],
        totalCount: count || 0
      };

      this.cache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('getTopicsByCategorySlugWithSort: Error:', error);
      return { topics: [], totalCount: 0 };
    }
  }

  private buildTopicsQuery(categoryId: string, offset: number, limit: number, sortBy: string) {
    let query = supabase
      .from('forum_topics')
      .select(`
        *,
        category:forum_categories!forum_topics_category_id_fkey(title, slug, icon, color),
        created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
      `)
      .eq('category_id', categoryId);

    // Always put pinned posts first
    query = query.order('is_pinned', { ascending: false });

    // Then apply the requested sorting
    switch (sortBy) {
      case 'newest':
        query = query.order('created_at', { ascending: false });
        break;
      case 'oldest':
        query = query.order('created_at', { ascending: true });
        break;
      case 'most_popular':
        query = query.order('like_count', { ascending: false });
        break;
      case 'most_active':
      default:
        query = query.order('last_activity_at', { ascending: false });
        break;
    }

    return query.range(offset, offset + limit - 1);
  }

  // Get the first image attachment from a topic's first post
  async getTopicPreviewImage(topicId: string): Promise<string | null> {
    const cacheKey = `topic-image:${topicId}`;
    const cached = this.cache.get<string | null>(cacheKey);
    if (cached !== undefined) return cached;

    try {
      // Get the first post of the topic
      const { data: firstPost, error } = await supabase
        .from('forum_posts')
        .select('attachments')
        .eq('topic_id', topicId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (error || !firstPost) {
        this.cache.set(cacheKey, null);
        return null;
      }

      // Find the first image attachment
      const attachments = firstPost.attachments || [];
      const imageAttachment = attachments.find((att: any) =>
        att.type === 'image' || (att.mime_type && att.mime_type.startsWith('image/'))
      );

      const imageUrl = imageAttachment?.url || null;
      this.cache.set(cacheKey, imageUrl);
      return imageUrl;
    } catch (error) {
      console.error('Error getting topic preview image:', error);
      this.cache.set(cacheKey, null);
      return null;
    }
  }

  // Get preview images for multiple topics (bulk operation for performance)
  async getTopicPreviewImages(topicIds: string[]): Promise<Record<string, string | null>> {
    const result: Record<string, string | null> = {};

    // Check cache first
    const uncachedIds: string[] = [];
    for (const topicId of topicIds) {
      const cacheKey = `topic-image:${topicId}`;
      const cached = this.cache.get<string | null>(cacheKey);
      if (cached !== undefined) {
        result[topicId] = cached;
      } else {
        uncachedIds.push(topicId);
      }
    }

    if (uncachedIds.length === 0) {
      return result;
    }

    try {
      // Get first posts for all uncached topics
      const { data: posts, error } = await supabase
        .from('forum_posts')
        .select('topic_id, attachments')
        .in('topic_id', uncachedIds)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by topic_id and get first post for each
      const topicFirstPosts: Record<string, any> = {};
      posts?.forEach(post => {
        if (!topicFirstPosts[post.topic_id]) {
          topicFirstPosts[post.topic_id] = post;
        }
      });

      // Extract image URLs
      for (const topicId of uncachedIds) {
        const firstPost = topicFirstPosts[topicId];
        let imageUrl: string | null = null;

        if (firstPost?.attachments) {
          const attachments = firstPost.attachments || [];
          const imageAttachment = attachments.find((att: any) =>
            att.type === 'image' || (att.mime_type && att.mime_type.startsWith('image/'))
          );
          imageUrl = imageAttachment?.url || null;
        }

        result[topicId] = imageUrl;
        this.cache.set(`topic-image:${topicId}`, imageUrl);
      }

      return result;
    } catch (error) {
      console.error('Error getting topic preview images:', error);
      // Cache null values for failed topics
      for (const topicId of uncachedIds) {
        result[topicId] = null;
        this.cache.set(`topic-image:${topicId}`, null);
      }
      return result;
    }
  }

  // Topics
  async getAllTopics(page = 1, limit = 20): Promise<{ topics: ForumTopic[]; totalCount: number }> {
    const offset = (page - 1) * limit;

    const cacheKey = `all_topics:${page}:${limit}`;
    const cachedData = this.cache.get<{ topics: ForumTopic[]; totalCount: number }>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const [topicsQuery, countQuery] = await Promise.all([
      supabase
        .from('forum_topics')
        .select(`
          *,
          category:forum_categories!forum_topics_category_id_fkey(title, slug),
          created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
        `)
        .order('is_pinned', { ascending: false })
        .order('last_activity_at', { ascending: false })
        .range(offset, offset + limit - 1),

      supabase
        .from('forum_topics')
        .select('*', { count: 'exact', head: true })
    ]);

    const { data: topics, error: topicsError } = topicsQuery;
    const { count, error: countError } = countQuery;

    if (topicsError) throw topicsError;
    if (countError) throw countError;

    const result = {
      topics: topics || [],
      totalCount: count || 0
    };

    this.cache.set(cacheKey, result);
    return result;
  }

  async createTopic(topicData: CreateTopicData): Promise<ForumTopic> {
    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User must be authenticated to create topics');
    }

    // Extract poll data before database insert (polls are stored in separate table)
    const { poll, ...topicDataWithoutPoll } = topicData;

    const { data, error } = await supabase
      .from('forum_topics')
      .insert({
        ...topicDataWithoutPoll,
        created_by: user.id,
        slug: this.generateSlug(topicData.title)
      })
      .select(`
        *,
        category:forum_categories!forum_topics_category_id_fkey(title, slug, icon, color),
        created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
      `)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Failed to create topic');

    // Create poll if provided
    if (poll && poll.options.length >= 2) {
      try {
        await this.createPoll(data.id, poll.question, poll.options);
      } catch (pollError) {
        console.error('Failed to create poll:', pollError);
        // We don't fail the topic creation if poll creation fails, but we should probably alert
      }
    }

    this.clearCacheForCategory(topicData.category_id);
    return data;
  }

  async createPoll(topicId: string, question: string, options: string[]): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // 1. Create the poll
    const { data: poll, error: pollError } = await supabase
      .from('forum_polls')
      .insert({
        topic_id: topicId,
        question,
        created_by: user.id
      })
      .select()
      .single();

    if (pollError) throw pollError;

    // 2. Create options
    const optionsData = options.map((optionText, index) => ({
      poll_id: poll.id,
      option_text: optionText,
      option_order: index
    }));

    const { error: optionsError } = await supabase
      .from('forum_poll_options')
      .insert(optionsData);

    if (optionsError) throw optionsError;
  }

  async getPollByTopicId(topicId: string): Promise<{ poll: ForumPoll, options: ForumPollOption[], userVote?: string } | null> {
    const { data: poll, error: pollError } = await supabase
      .from('forum_polls')
      .select('*')
      .eq('topic_id', topicId)
      .single();

    if (pollError) {
      if (pollError.code === 'PGRST116') return null; // No poll found
      throw pollError;
    }

    const { data: options, error: optionsError } = await supabase
      .from('forum_poll_options')
      .select('*')
      .eq('poll_id', poll.id)
      .order('option_order', { ascending: true });

    if (optionsError) throw optionsError;

    // Check if current user has voted
    let userVote = undefined;
    const { data: { user } } = await supabase.auth.getUser();

    if (user) {
      const { data: vote } = await supabase
        .from('forum_poll_votes')
        .select('option_id')
        .eq('poll_id', poll.id)
        .eq('user_id', user.id)
        .single();

      if (vote) {
        userVote = vote.option_id;
      }
    }

    return {
      poll,
      options: options || [],
      userVote
    };
  }

  /**
   * Optimized version that accepts user to avoid duplicate auth calls
   */
  async getPollByTopicIdWithUser(topicId: string, user: any | null): Promise<{ poll: ForumPoll, options: ForumPollOption[], userVote?: string } | null> {
    const { data: poll, error: pollError } = await supabase
      .from('forum_polls')
      .select('*')
      .eq('topic_id', topicId)
      .single();

    if (pollError) {
      if (pollError.code === 'PGRST116') return null; // No poll found
      throw pollError;
    }

    const { data: options, error: optionsError } = await supabase
      .from('forum_poll_options')
      .select('*')
      .eq('poll_id', poll.id)
      .order('option_order', { ascending: true });

    if (optionsError) throw optionsError;

    // Check if current user has voted
    let userVote = undefined;
    if (user) {
      const { data: vote } = await supabase
        .from('forum_poll_votes')
        .select('option_id')
        .eq('poll_id', poll.id)
        .eq('user_id', user.id)
        .single();

      if (vote) {
        userVote = vote.option_id;
      }
    }

    return {
      poll,
      options: options || [],
      userVote
    };
  }

  async voteOnPoll(pollId: string, optionId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Use the RPC function we saw in the migration file
    const { error } = await supabase.rpc('vote_on_poll', {
      p_poll_id: pollId,
      p_option_id: optionId
    });

    if (error) throw error;
  }

  async createTopicByCategorySlug(categorySlug: string, topicData: Omit<CreateTopicData, 'category_id'>): Promise<ForumTopic> {
    console.log('createTopicByCategorySlug: Creating topic for slug:', categorySlug);

    // First get the category to find its ID
    const category = await this.getCategoryBySlugDirect(categorySlug);
    if (!category?.id) {
      throw new Error(`Category not found for slug: ${categorySlug}`);
    }

    console.log('createTopicByCategorySlug: Found category:', category.id, category.title);

    // Create topic with the category ID
    return this.createTopic({
      ...topicData,
      category_id: category.id
    });
  }

  async getRecentDiscussions(limit: number = 5): Promise<Array<{
    id: string;
    title: string;
    created_at: string;
    post_count: number;
    author: string;
    author_avatar_url?: string;
    category_slug: string;
    category_title: string;
  }>> {
    const cacheKey = `recent_discussions:${limit}`;
    const cachedData = this.cache.get<Array<{
      id: string;
      title: string;
      created_at: string;
      post_count: number;
      author: string;
      author_avatar_url?: string;
      category_slug: string;
      category_title: string;
    }>>(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    // Use request deduplication
    return requestDeduplicator.dedupe(cacheKey, async () => {
      try {
        const { data, error } = await supabase
          .from('forum_topics')
          .select(`
            id,
            title,
            created_at,
            post_count,
            category:forum_categories!forum_topics_category_id_fkey(title, slug),
            created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
          `)
          .order('last_activity_at', { ascending: false })
          .limit(limit);

        if (error) throw error;

        // Transform data to expected format
        const discussions = (data || []).map(topic => {
          const user = Array.isArray(topic.created_by_user) ? topic.created_by_user[0] : topic.created_by_user;
          const category = Array.isArray(topic.category) ? topic.category[0] : topic.category;

          return {
            id: topic.id,
            title: topic.title,
            created_at: topic.created_at,
            post_count: topic.post_count,
            author: user?.display_name || user?.username || 'Anonymous',
            author_avatar_url: user?.avatar_url,
            category_slug: category?.slug || '',
            category_title: category?.title || 'Unknown'
          };
        });

        this.cache.set(cacheKey, discussions, 5 * 60 * 1000); // Cache for 5 minutes
        return discussions;
      } catch (error) {
        console.error('Error fetching recent discussions:', error);
        return [];
      }
    });
  }

  async getTopUsers(limit: number = 5): Promise<any[]> {
    const cacheKey = `top_users:${limit}`;
    const cachedData = this.cache.get<any[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Use request deduplication
    return requestDeduplicator.dedupe(cacheKey, async () => {
      try {
        // Single optimized query to get users with their post counts
        const { data, error } = await supabase
          .from('users')
          .select(`
            id,
            username,
            display_name,
            avatar_url,
            post_count:forum_posts(count)
          `)
          .eq('forum_posts.is_deleted', false)
          .order('forum_posts(count)', { ascending: false })
          .limit(limit);

        if (error) {
          // Fallback to simpler query if the above fails
          console.warn('Optimized query failed, using fallback:', error);
          const { data: users, error: fallbackError } = await supabase
            .from('users')
            .select('id, username, display_name, avatar_url')
            .limit(limit * 3);

          if (fallbackError) throw fallbackError;
          if (!users) return [];

          // Use a single aggregated query for post counts
          const { data: postCounts, error: countError } = await supabase
            .from('forum_posts')
            .select('user_id, id')
            .eq('is_deleted', false)
            .in('user_id', users.map(u => u.id));

          if (countError) throw countError;

          // Count posts per user
          const userPostCounts: Record<string, number> = {};
          (postCounts || []).forEach(post => {
            userPostCounts[post.user_id] = (userPostCounts[post.user_id] || 0) + 1;
          });

          const topUsers = users
            .map(user => ({
              id: user.id,
              username: user.username || 'Anonymous',
              display_name: user.display_name,
              avatar_url: user.avatar_url,
              post_count: userPostCounts[user.id] || 0,
              reputation: Math.min(100, (userPostCounts[user.id] || 0) * 2 + Math.floor(Math.random() * 20))
            }))
            .sort((a, b) => b.post_count - a.post_count)
            .slice(0, limit);

          this.cache.set(cacheKey, topUsers, 10 * 60 * 1000); // Cache for 10 minutes
          return topUsers;
        }

        const topUsers = (data || []).map(user => {
          const postCount = Array.isArray(user.post_count) ? user.post_count.length : (user.post_count || 0);
          return {
            id: user.id,
            username: user.username || 'Anonymous',
            display_name: user.display_name,
            avatar_url: user.avatar_url,
            post_count: postCount,
            reputation: Math.min(100, postCount * 2 + Math.floor(Math.random() * 20))
          };
        });

        this.cache.set(cacheKey, topUsers, 10 * 60 * 1000); // Cache for 10 minutes
        return topUsers;
      } catch (error) {
        console.error('Error fetching top users:', error);
        return [];
      }
    });
  }

  async getPosts(topicId: string, page = 1, limit = 10): Promise<{ posts: ForumPost[]; totalCount: number }> {
    const offset = (page - 1) * limit;

    const [postsQuery, countQuery] = await Promise.all([
      supabase
        .from('forum_posts')
        .select(`
          *,
          author:users!forum_posts_user_id_fkey(id, username, display_name, avatar_url, created_at)
        `)
        .eq('topic_id', topicId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true })
        .range(offset, offset + limit - 1),

      supabase
        .from('forum_posts')
        .select('*', { count: 'exact', head: true })
        .eq('topic_id', topicId)
        .eq('is_deleted', false)
    ]);

    const { data: posts, error: postsError } = postsQuery;
    const { count, error: countError } = countQuery;

    if (postsError) throw postsError;
    if (countError) throw countError;

    return {
      posts: posts || [],
      totalCount: count || 0
    };
  }

  async getPostsWithEngagement(topicId: string, page = 1, limit = 10): Promise<{ posts: (ForumPost & { liked_by_user?: boolean })[]; totalCount: number }> {
    const { posts, totalCount } = await this.getPosts(topicId, page, limit);

    // Get actual post counts for all authors
    const authorIds = posts.map(p => p.user_id).filter(id => id);
    const uniqueAuthorIds = [...new Set(authorIds)];

    let authorPostCounts = new Map<string, number>();
    if (uniqueAuthorIds.length > 0) {
      const { data: postCounts } = await supabase
        .from('forum_posts')
        .select('user_id')
        .in('user_id', uniqueAuthorIds)
        .eq('is_deleted', false);

      // Count posts per user
      if (postCounts) {
        postCounts.forEach(post => {
          const currentCount = authorPostCounts.get(post.user_id) || 0;
          authorPostCounts.set(post.user_id, currentCount + 1);
        });
      }
    }

    // If user is logged in, check which posts they've liked
    const currentUser = await supabase.auth.getUser();
    if (currentUser.data.user) {
      console.log('🔍 Checking liked posts for user:', currentUser.data.user.id);

      const { data: likedPosts } = await supabase
        .from('forum_post_likes')
        .select('post_id')
        .eq('user_id', currentUser.data.user.id)
        .in('post_id', posts.map(p => p.id));

      const likedPostIds = new Set(likedPosts?.map(l => l.post_id) || []);
      console.log('❤️ User has liked posts:', Array.from(likedPostIds));

      const postsWithEngagement = posts.map(post => ({
        ...post,
        liked_by_user: likedPostIds.has(post.id),
        author: post.author ? {
          ...post.author,
          post_count: post.user_id ? (authorPostCounts.get(post.user_id) || 0) : 0
        } : post.author
      }));

      console.log('📊 Posts with like status:', postsWithEngagement.map(p => ({
        id: p.id,
        liked_by_user: p.liked_by_user,
        like_count: p.like_count
      })));

      return { posts: postsWithEngagement, totalCount };
    }

    // Add post counts even for non-logged-in users
    const postsWithCounts = posts.map(post => ({
      ...post,
      liked_by_user: false,
      author: post.author ? {
        ...post.author,
        post_count: post.user_id ? (authorPostCounts.get(post.user_id) || 0) : 0
      } : post.author
    }));

    return { posts: postsWithCounts, totalCount };
  }

  /**
   * Optimized version that accepts user to avoid duplicate auth calls
   */
  async getPostsWithEngagementWithUser(topicId: string, page = 1, limit = 10, user: any | null): Promise<{ posts: (ForumPost & { liked_by_user?: boolean })[]; totalCount: number }> {
    const { posts, totalCount } = await this.getPosts(topicId, page, limit);

    // Get actual post counts for all authors
    const authorIds = posts.map(p => p.user_id).filter(id => id);
    const uniqueAuthorIds = [...new Set(authorIds)];

    let authorPostCounts = new Map<string, number>();
    if (uniqueAuthorIds.length > 0) {
      const { data: postCounts } = await supabase
        .from('forum_posts')
        .select('user_id')
        .in('user_id', uniqueAuthorIds)
        .eq('is_deleted', false);

      // Count posts per user
      if (postCounts) {
        postCounts.forEach(post => {
          const currentCount = authorPostCounts.get(post.user_id) || 0;
          authorPostCounts.set(post.user_id, currentCount + 1);
        });
      }
    }

    // If user is logged in, check which posts they've liked
    if (user) {
      console.log('🔍 Checking liked posts for user:', user.id);

      const { data: likedPosts } = await supabase
        .from('forum_post_likes')
        .select('post_id')
        .eq('user_id', user.id)
        .in('post_id', posts.map(p => p.id));

      const likedPostIds = new Set(likedPosts?.map(l => l.post_id) || []);
      console.log('❤️ User has liked posts:', Array.from(likedPostIds));

      const postsWithEngagement = posts.map(post => ({
        ...post,
        liked_by_user: likedPostIds.has(post.id),
        author: post.author ? {
          ...post.author,
          post_count: post.user_id ? (authorPostCounts.get(post.user_id) || 0) : 0
        } : post.author
      }));

      console.log('📊 Posts with like status:', postsWithEngagement.map(p => ({
        id: p.id,
        liked_by_user: p.liked_by_user,
        like_count: p.like_count
      })));

      return { posts: postsWithEngagement, totalCount };
    }

    // Add post counts even for non-logged-in users
    const postsWithCounts = posts.map(post => ({
      ...post,
      liked_by_user: false,
      author: post.author ? {
        ...post.author,
        post_count: post.user_id ? (authorPostCounts.get(post.user_id) || 0) : 0
      } : post.author
    }));

    return { posts: postsWithCounts, totalCount };
  }



  /**
   * Increment the view count for a topic
   * Uses sessionStorage to prevent counting the same user viewing the same topic multiple times in one session
   * @param topicId - The ID of the topic to increment views for
   */
  async incrementTopicViews(topicId: string): Promise<void> {
    // Use sessionStorage to prevent counting multiple views per session
    const viewedTopicsKey = 'viewed_forum_topics';
    try {
      const viewedTopics = JSON.parse(sessionStorage.getItem(viewedTopicsKey) || '[]');

      // Check if this topic was already viewed in this session
      if (viewedTopics.includes(topicId)) {
        console.log('📊 Topic already viewed in this session, skipping view count increment');
        return;
      }

      // Call the database function to increment view count
      const { error } = await supabase.rpc('increment_topic_views', {
        topic_id_param: topicId
      });

      if (error) {
        console.error('Error incrementing topic views:', error);
        // Don't throw - view count is non-critical
        return;
      }

      // Mark this topic as viewed in this session
      viewedTopics.push(topicId);
      sessionStorage.setItem(viewedTopicsKey, JSON.stringify(viewedTopics));
      console.log('📊 Topic view count incremented for:', topicId);
    } catch (err) {
      console.error('Error tracking topic view:', err);
      // Non-critical error, don't throw
    }
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  // Users
  async getUserProfile(userId: string): Promise<any> {
    const { data, error } = await supabase
      .from('users')
      .select(`
        id,
        username,
        display_name,
        avatar_url,
        bio,
        created_at,
        updated_at,
        role,
        is_active,
        last_login,
        forum_posts:forum_posts!forum_posts_user_id_fkey(*),
        forum_comments:forum_comments!forum_comments_user_id_fkey(*)
      `)
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  }

  async updateUserProfile(userId: string, profileData: any): Promise<any> {
    const { data, error } = await supabase
      .from('users')
      .update(profileData)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Posts
  async getPostById(postId: string): Promise<ForumPost | null> {
    const cacheKey = `post:${postId}`;
    const cachedData = this.cache.get<ForumPost>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const { data, error } = await supabase
      .from('forum_posts')
      .select(`
        *,
        author:users!forum_posts_user_id_fkey(id, username, display_name, avatar_url),
        topic:forum_topics!forum_posts_topic_id_fkey(title, category_id)
      `)
      .eq('id', postId)
      .eq('is_deleted', false)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    this.cache.set(cacheKey, data);
    return data;
  }

  // Get fresh post data for real-time updates (bypasses cache)
  async getPostByIdFresh(id: string): Promise<ForumPost | null> {
    const { data, error } = await supabase
      .from('forum_posts')
      .select(`
        *,
        author:users!forum_posts_user_id_fkey(id, username, display_name, avatar_url, created_at)
      `)
      .eq('id', id)
      .eq('is_deleted', false)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    // Get the actual post count for this author
    if (data && data.user_id) {
      const { data: postCountData } = await supabase
        .from('forum_posts')
        .select('id')
        .eq('user_id', data.user_id)
        .eq('is_deleted', false);

      if (data.author) {
        data.author.post_count = postCountData?.length || 0;
      }
    }

    return data;
  }

  async createPost(postData: CreatePostData): Promise<ForumPost> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Calculate depth for threaded replies
    let depth = 0;
    if (postData.parent_post_id) {
      const { data: parentPost } = await supabase
        .from('forum_posts')
        .select('depth')
        .eq('id', postData.parent_post_id)
        .single();

      if (parentPost) {
        depth = Math.min(parentPost.depth + 1, 5); // Max depth of 5
      }
    }

    const { data, error } = await supabase
      .from('forum_posts')
      .insert({
        ...postData,
        user_id: user.id,
        depth,
        attachments: postData.attachments || []
      })
      .select(`
        *,
        author:users!forum_posts_user_id_fkey(username, display_name, avatar_url)
      `)
      .single();

    if (error) throw error;

    // Invalidate cache - clear all posts cache entries for this topic
    this.clearCacheForPost(data.id, postData.topic_id);

    // Also update topic's last activity - with fallback if RPC doesn't exist
    try {
      await supabase.rpc('increment_topic_post_count', {
        topic_id_param: postData.topic_id,
        user_id_param: user.id
      });
    } catch (rpcError: any) {
      // Fallback to direct update if RPC function doesn't exist
      console.log('RPC function not found, using direct update:', rpcError);
      await supabase
        .from('forum_topics')
        .update({
          last_activity_at: new Date().toISOString(),
          last_activity_by: user.id
        })
        .eq('id', postData.topic_id);
    }

    return data;
  }

  async updatePost(id: string, updates: UpdatePostData): Promise<ForumPost> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('forum_posts')
      .update({
        ...updates,
        is_edited: true,
        edited_at: new Date().toISOString(),
        edited_by: user.id
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    this.cache.delete(`post:${id}`);
    this.cache.delete(`posts:*`);
    return data;
  }

  async deletePost(id: string, reason?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('forum_posts')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: user.id,
        delete_reason: reason
      })
      .eq('id', id);

    if (error) throw error;

    // Invalidate cache
    this.cache.delete(`post:${id}`);
    this.cache.delete(`posts:*`);
  }

  // Likes
  async likePost(postId: string): Promise<void> {
    console.log('🔄 ForumService.likePost called for:', postId);

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('❌ Authentication error in likePost:', authError);
      throw new Error('Authentication failed');
    }
    if (!user) {
      console.error('❌ No user found in likePost');
      throw new Error('Not authenticated');
    }

    console.log('👤 User attempting to like post:', { userId: user.id, postId });

    // First check if the post exists and is not deleted
    const { data: postExists, error: postCheckError } = await supabase
      .from('forum_posts')
      .select('id, is_deleted')
      .eq('id', postId)
      .eq('is_deleted', false)
      .single();

    if (postCheckError || !postExists) {
      console.error('❌ Post not found or deleted:', { postId, error: postCheckError });
      throw new Error('Post not found');
    }

    // Check if already liked to prevent duplicates (client-side check)
    const { data: existingLike } = await supabase
      .from('forum_post_likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', user.id)
      .single();

    if (existingLike) {
      console.warn('⚠️ User has already liked this post:', { userId: user.id, postId });
      // Don't throw error, just return silently as this is an expected case
      return;
    }

    const { error } = await supabase
      .from('forum_post_likes')
      .insert({
        post_id: postId,
        user_id: user.id
      });

    if (error) {
      console.error('❌ Database error in likePost:', error);
      if (error.code === '23505') {
        // Duplicate key error - user already liked this post
        console.log('⚠️ Duplicate like ignored (race condition)');
        return; // Don't throw error for duplicates
      }
      throw error;
    }

    console.log('✅ Post liked successfully:', { userId: user.id, postId });
  }

  async unlikePost(postId: string): Promise<void> {
    console.log('🔄 ForumService.unlikePost called for:', postId);

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('❌ Authentication error in unlikePost:', authError);
      throw new Error('Authentication failed');
    }
    if (!user) {
      console.error('❌ No user found in unlikePost');
      throw new Error('Not authenticated');
    }

    console.log('👤 User attempting to unlike post:', { userId: user.id, postId });

    // Check if the like exists before trying to delete
    const { data: existingLike } = await supabase
      .from('forum_post_likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', user.id)
      .single();

    if (!existingLike) {
      console.warn('⚠️ No like found to remove:', { userId: user.id, postId });
      // Don't throw error, just return silently as this might be a race condition
      return;
    }

    const { error } = await supabase
      .from('forum_post_likes')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', user.id);

    if (error) {
      console.error('❌ Database error in unlikePost:', error);
      throw error;
    }

    console.log('✅ Post unliked successfully:', { userId: user.id, postId });
  }

  async getPostLikes(postId: string): Promise<ForumPostLike[]> {
    const { data, error } = await supabase
      .from('forum_post_likes')
      .select(`
        *,
        user:users!forum_post_likes_user_id_fkey(username, display_name, avatar_url)
      `)
      .eq('post_id', postId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Reports
  async reportPost(postId: string, reason: string, description?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('forum_post_reports')
      .insert({
        post_id: postId,
        reported_by: user.id,
        reason,
        description
      });

    if (error) throw error;

    // Update post report count
    await supabase.rpc('increment_post_reports', { post_id: postId });
  }

  async getReports(status?: string): Promise<ForumPostReport[]> {
    let query = supabase
      .from('forum_post_reports')
      .select(`
        *,
        post:forum_posts!forum_post_reports_post_id_fkey(id, content, topic_id),
        reported_by_user:users!forum_post_reports_reported_by_fkey(username, display_name),
        reviewed_by_user:users!forum_post_reports_reviewed_by_fkey(username, display_name)
      `)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  async resolveReport(reportId: string, resolutionNotes?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('forum_post_reports')
      .update({
        status: 'resolved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        resolution_notes: resolutionNotes
      })
      .eq('id', reportId);

    if (error) throw error;
  }

  // Search
  async searchForum(query: string, filters?: {
    categories?: string[];
    tags?: string[];
    dateRange?: { from: string; to: string };
  }): Promise<{ topics: ForumTopic[]; posts: ForumPost[] }> {
    const searchTerms = query.split(' ').filter(term => term.length > 2);

    if (searchTerms.length === 0) {
      return { topics: [], posts: [] };
    }

    // Search topics
    let topicsQuery = supabase
      .from('forum_topics')
      .select(`
        *,
        category:forum_categories!forum_topics_category_id_fkey(title, slug, icon, color),
        created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
      `)
      .textSearch('title', searchTerms.join(' & '), {
        type: 'websearch',
        config: 'english'
      });

    // Search posts
    let postsQuery = supabase
      .from('forum_posts')
      .select(`
        *,
        topic:forum_topics!forum_posts_topic_id_fkey(title, category_id),
        user:users!forum_posts_user_id_fkey(username, display_name, avatar_url)
      `)
      .textSearch('content', searchTerms.join(' & '), {
        type: 'websearch',
        config: 'english'
      })
      .eq('is_deleted', false);

    // Apply filters
    if (filters?.categories?.length) {
      topicsQuery = topicsQuery.in('category_id', filters.categories);
      postsQuery = postsQuery.in('topic.category_id', filters.categories);
    }

    if (filters?.dateRange) {
      topicsQuery = topicsQuery
        .gte('created_at', filters.dateRange.from)
        .lte('created_at', filters.dateRange.to);
      postsQuery = postsQuery
        .gte('created_at', filters.dateRange.from)
        .lte('created_at', filters.dateRange.to);
    }

    const [topicsResult, postsResult] = await Promise.all([
      topicsQuery.limit(20),
      postsQuery.limit(20)
    ]);

    if (topicsResult.error) throw topicsResult.error;
    if (postsResult.error) throw postsResult.error;

    return {
      topics: topicsResult.data || [],
      posts: postsResult.data || []
    };
  }

  // User preferences
  async getUserPreferences(): Promise<any> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('user_forum_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    // Return default preferences if none found
    return data || {
      user_id: user.id,
      favorite_categories: [],
      notification_settings: {
        email_notifications: true,
        push_notifications: true,
        mention_notifications: true,
        reply_notifications: true,
        like_notifications: false,
        daily_digest: true
      },
      timezone: 'UTC',
      posts_per_page: 20,
      theme_preference: 'system'
    };
  }

  async updateUserPreferences(preferences: any): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_forum_preferences')
      .upsert({
        user_id: user.id,
        ...preferences,
        updated_at: new Date().toISOString()
      });

    if (error) throw error;
  }

  // Notifications
  async getNotifications(page = 1, limit = 20): Promise<{ notifications: ForumNotification[]; totalCount: number }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const offset = (page - 1) * limit;

    const [notificationsQuery, countQuery] = await Promise.all([
      supabase
        .from('forum_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1),

      supabase
        .from('forum_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
    ]);

    const { data: notifications, error: notificationsError } = notificationsQuery;
    const { count, error: countError } = countQuery;

    if (notificationsError) throw notificationsError;
    if (countError) throw countError;

    return {
      notifications: notifications || [],
      totalCount: count || 0
    };
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from('forum_notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', notificationId);

    if (error) throw error;
  }

  async markAllNotificationsAsRead(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('forum_notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (error) throw error;
  }

  // Topic subscriptions
  async subscribeToTopic(topicId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('forum_topic_subscriptions')
      .insert({
        topic_id: topicId,
        user_id: user.id
      });

    if (error && error.code !== '23505') { // Ignore duplicate key error
      throw error;
    }
  }

  async unsubscribeFromTopic(topicId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('forum_topic_subscriptions')
      .delete()
      .eq('topic_id', topicId)
      .eq('user_id', user.id);

    if (error) throw error;
  }

  // User badges
  async getUserBadges(userId: string): Promise<UserForumBadge[]> {
    const { data, error } = await supabase
      .from('user_forum_badges')
      .select('*')
      .eq('user_id', userId)
      .eq('is_visible', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // Forum rules
  async getForumRules(categoryId?: string): Promise<ForumRule[]> {
    let query = supabase
      .from('forum_rules')
      .select('*')
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (categoryId) {
      query = query.or(`category_id.is.null,category_id.eq.${categoryId}`);
    } else {
      query = query.is('category_id', null);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // Moderation methods
  async getReportedPosts(): Promise<ForumPost[]> {
    const { data, error } = await supabase
      .from('forum_posts')
      .select(`
        *,
        reports:forum_post_reports!forum_post_reports_post_id_fkey(*),
        user:users!forum_posts_user_id_fkey(username, display_name, avatar_url),
        topic:forum_topics!forum_posts_topic_id_fkey(title, category_id)
      `)
      .eq('is_reported', true)
      .gte('report_count', 1)
      .order('report_count', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(post => ({
      ...post,
      severity: post.report_count >= 5 ? 'critical' :
        post.report_count >= 3 ? 'high' :
          post.report_count >= 2 ? 'medium' : 'low'
    })) || [];
  }

  async getPendingTopics(): Promise<ForumTopic[]> {
    const { data, error } = await supabase
      .from('forum_topics')
      .select(`
        *,
        category:forum_categories!forum_topics_category_id_fkey(title, slug, icon, color),
        created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
      `)
      .eq('requires_approval', true)
      .is('approved_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getModeratorActions(): Promise<ModerationAction[]> {
    const { data, error } = await supabase
      .from('forum_activity_log')
      .select(`
        *,
        moderator:users!forum_activity_log_user_id_fkey(username, display_name)
      `)
      .in('action', ['approve', 'reject', 'delete_post', 'ban', 'warn', 'lock_topic', 'pin_topic'])
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;

    return data?.map(log => ({
      id: log.id,
      action_type: log.action,
      target_entity_id: log.target_id,
      target_entity_type: log.target_type,
      reason: log.reason || 'No reason provided',
      moderator_id: log.user_id,
      moderator_username: log.moderator?.username || 'Unknown',
      created_at: log.created_at,
      duration_hours: log.duration_hours
    })) || [];
  }

  async getFlaggedUsers(): Promise<any[]> {
    const { data, error } = await supabase
      .from('users')
      .select(`
        id,
        username,
        avatar_url,
        created_at,
        post_count:forum_posts!forum_posts_user_id_fkey(count),
        report_count:forum_post_reports!forum_post_reports_reported_by_fkey(count),
        bans:user_forum_bans!user_forum_bans_user_id_fkey(*)
      `)
      .gte('forum_post_reports.count', 3) // Users with 3+ reports against their posts
      .order('forum_post_reports.count', { ascending: false });

    if (error) throw error;

    return data?.map(user => ({
      ...user,
      post_count: user.post_count?.[0]?.count || 0,
      report_count: user.report_count?.[0]?.count || 0
    })) || [];
  }

  async approvePost(postId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Update the post
    const { error: updateError } = await supabase
      .from('forum_posts')
      .update({
        is_approved: true,
        approved_at: new Date().toISOString(),
        approved_by: user.id,
        is_reported: false
      })
      .eq('id', postId);

    if (updateError) throw updateError;

    // Log the action
    await this.logModerationAction({
      action: 'approve',
      target_id: postId,
      target_type: 'post',
      reason: 'Content approved after review'
    });

    // Dismiss related reports
    await supabase
      .from('forum_post_reports')
      .update({
        status: 'dismissed',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id
      })
      .eq('post_id', postId)
      .eq('status', 'pending');
  }

  async rejectPost(postId: string, reason: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Soft delete the post
    const { error: updateError } = await supabase
      .from('forum_posts')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: user.id,
        delete_reason: reason,
        is_reported: false
      })
      .eq('id', postId);

    if (updateError) throw updateError;

    // Log the action
    await this.logModerationAction({
      action: 'reject',
      target_id: postId,
      target_type: 'post',
      reason: reason
    });

    // Resolve related reports
    await supabase
      .from('forum_post_reports')
      .update({
        status: 'resolved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        resolution_notes: `Post rejected: ${reason}`
      })
      .eq('post_id', postId)
      .eq('status', 'pending');
  }

  async banUser(userId: string, durationHours: number, reason: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const expiresAt = new Date(Date.now() + durationHours * 60 * 60 * 1000);

    // Create ban record
    const { error: banError } = await supabase
      .from('user_forum_bans')
      .insert({
        user_id: userId,
        banned_by: user.id,
        reason: reason,
        expires_at: expiresAt.toISOString(),
        is_active: true,
        created_at: new Date().toISOString()
      });

    if (banError) throw banError;

    // Log the action
    await this.logModerationAction({
      action: 'ban',
      target_id: userId,
      target_type: 'user',
      reason: reason,
      duration_hours: durationHours
    });
  }

  private async logModerationAction(actionData: {
    action: string;
    target_id: string;
    target_type: string;
    reason: string;
    duration_hours?: number;
  }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('forum_activity_log')
      .insert({
        user_id: user.id,
        action: actionData.action,
        target_id: actionData.target_id,
        target_type: actionData.target_type,
        reason: actionData.reason,
        duration_hours: actionData.duration_hours,
        created_at: new Date().toISOString()
      });
  }

  // Admin topic management
  async pinTopic(topicId: string, shouldPin: boolean = true): Promise<void> {
    try {
      const { error } = await supabase.rpc('pin_topic', {
        topic_id: topicId,
        should_pin: shouldPin
      });

      if (error) throw error;

      // Clear cache for the topic and related data
      this.cache.clear();
    } catch (error) {
      console.error('Error pinning/unpinning topic:', error);
      throw error;
    }
  }

  async unpinTopic(topicId: string): Promise<void> {
    return this.pinTopic(topicId, false);
  }

  async toggleTopicPin(topicId: string): Promise<boolean> {
    try {
      const { data: newPinStatus, error } = await supabase.rpc('toggle_topic_pin', {
        topic_id: topicId
      });

      if (error) throw error;

      // Clear cache for the topic and related data
      this.cache.clear();

      return newPinStatus;
    } catch (error) {
      console.error('Error toggling topic pin:', error);
      throw error;
    }
  }

  // Helper method to check if user is admin
  async isUserAdmin(userId?: string): Promise<boolean> {
    try {
      const targetUserId = userId || supabase.auth.getUser().then(({ data }) => data.user?.id);
      if (!targetUserId) return false;

      const { data: user, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', typeof targetUserId === 'string' ? targetUserId : await targetUserId)
        .single();

      if (error || !user) return false;
      return user.role === 'admin';
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  /**
   * Optimized method to fetch topic with poll and posts in parallel
   * This significantly reduces loading time by combining multiple queries
   */
  async getTopicWithPollAndPosts(
    topicId: string,
    page = 1,
    limit = 20
  ): Promise<{
    topic: ForumTopic | null;
    poll: { poll: ForumPoll, options: ForumPollOption[], userVote?: string } | null;
    posts: (ForumPost & { liked_by_user?: boolean })[];
    totalPosts: number;
  }> {
    try {
      // Use getSession instead of getUser for instant cached access (0ms vs 500ms)
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      const offset = (page - 1) * limit;

      // BATCH 1: Fetch topic, poll, posts, and counts in parallel
      const [
        topicResult,
        pollResult,
        postsResult,
        postsCountResult
      ] = await Promise.all([
        // 1. Fetch topic with category
        supabase
          .from('forum_topics')
          .select(`
            *,
            category:forum_categories!forum_topics_category_id_fkey(*),
            created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
          `)
          .eq('id', topicId)
          .single(),

        // 2. Fetch poll by topic_id
        supabase
          .from('forum_polls')
          .select('*')
          .eq('topic_id', topicId)
          .single()
          .then(r => r.error?.code === 'PGRST116' ? { data: null, error: null } : r),

        // 3. Fetch posts
        supabase
          .from('forum_posts')
          .select(`
            *,
            author:users!forum_posts_user_id_fkey(id, username, display_name, avatar_url, created_at)
          `)
          .eq('topic_id', topicId)
          .eq('is_deleted', false)
          .order('created_at', { ascending: true })
          .range(offset, offset + limit - 1),

        // 4. Get total post count
        supabase
          .from('forum_posts')
          .select('*', { count: 'exact', head: true })
          .eq('topic_id', topicId)
          .eq('is_deleted', false)
      ]);

      // Handle topic result
      if (topicResult.error) {
        if (topicResult.error.code === 'PGRST116') {
          return { topic: null, poll: null, posts: [], totalPosts: 0 };
        }
        throw topicResult.error;
      }

      const posts = postsResult.data || [];
      const totalCount = postsCountResult.count || 0;
      const poll = pollResult.data;

      // BATCH 2: Now fetch poll details, author counts, and likes in parallel
      // (poll options/votes need poll_id, author counts/likes need post IDs)
      const authorIds = posts.map(p => p.user_id).filter(id => id);
      const uniqueAuthorIds = [...new Set(authorIds)];
      const postIds = posts.map(p => p.id);

      const [
        pollOptionsResult,
        pollVoteResult,
        authorCountsResult,
        likedPostsResult
      ] = await Promise.all([
        // 5. Fetch poll options (need poll_id)
        poll
          ? supabase
            .from('forum_poll_options')
            .select('*')
            .eq('poll_id', poll.id)
            .order('option_order', { ascending: true })
          : Promise.resolve({ data: [], error: null }),

        // 6. Fetch user's vote (need poll_id)
        poll && user
          ? supabase
            .from('forum_poll_votes')
            .select('option_id')
            .eq('poll_id', poll.id)
            .eq('user_id', user.id)
            .single()
            .then(r => r.error?.code === 'PGRST116' ? { data: null, error: null } : r)
          : Promise.resolve({ data: null, error: null }),

        // 7. Fetch author post counts (need author IDs)
        uniqueAuthorIds.length > 0
          ? supabase
            .from('forum_posts')
            .select('user_id')
            .in('user_id', uniqueAuthorIds)
            .eq('is_deleted', false)
          : Promise.resolve({ data: [], error: null }),

        // 8. Fetch liked posts (need post IDs)
        user && postIds.length > 0
          ? supabase
            .from('forum_post_likes')
            .select('post_id')
            .eq('user_id', user.id)
            .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      // Process author post counts
      const authorPostCounts = new Map<string, number>();
      if (authorCountsResult.data) {
        authorCountsResult.data.forEach((post: any) => {
          const currentCount = authorPostCounts.get(post.user_id) || 0;
          authorPostCounts.set(post.user_id, currentCount + 1);
        });
      }

      // Process liked posts
      const likedPostIds = new Set(likedPostsResult.data?.map((l: any) => l.post_id) || []);

      // Enhance posts with engagement data
      const postsWithEngagement = posts.map(post => ({
        ...post,
        liked_by_user: likedPostIds.has(post.id),
        author: post.author ? {
          ...post.author,
          post_count: post.user_id ? (authorPostCounts.get(post.user_id) || 0) : 0
        } : post.author
      }));

      // Build poll response
      let pollResponse = null;
      if (poll) {
        pollResponse = {
          poll: poll,
          options: pollOptionsResult.data || [],
          userVote: pollVoteResult.data?.option_id
        };
      }

      return {
        topic: topicResult.data,
        poll: pollResponse,
        posts: postsWithEngagement,
        totalPosts: totalCount
      };
    } catch (error) {
      console.error('Error fetching topic with poll and posts:', error);
      return { topic: null, poll: null, posts: [], totalPosts: 0 };
    }
  }

  async getTopicById(id: string): Promise<ForumTopic | null> {
    try {
      const { data, error } = await supabase
        .from('forum_topics')
        .select(`
          *,
          category:forum_categories!forum_topics_category_id_fkey(title, slug, icon, color),
          created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
        `)
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching topic by ID:', error);
      return null;
    }
  }
}

export const forumService = new ForumService();
